import { useEffect, useState } from "react";
import { useNotify, usePermissions, useRecordContext, useRefresh, useUpdate } from "react-admin";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { hasPermission } from "@/utils/permission-utils";
import { normalizeBookingPhone, validateBookingPhone } from "./booking-phone";

export function HallBookingsBookingPhoneEdit() {
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading: saving }] = useUpdate();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !record) return;
    setPhone(typeof record.phone === "string" ? record.phone : "");
    setError("");
  }, [open, record]);

  if (!record?.id || !hasPermission(permissions, "bookings.edit")) {
    return null;
  }

  const handleSave = () => {
    const validationError = validateBookingPhone(phone);
    if (validationError) {
      setError(validationError);
      return;
    }

    update(
      "bookings",
      {
        id: String(record.id),
        data: { phone: normalizeBookingPhone(phone) },
        previousData: record,
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Phone number updated", { type: "success" });
          refresh();
          setOpen(false);
        },
        onError: (err) => {
          notify(err instanceof Error ? err.message : "Could not update phone number", {
            type: "error",
          });
        },
      }
    );
  };

  return (
    <>
      <Tooltip title="Edit phone">
        <IconButton
          size="small"
          aria-label="Edit phone"
          onClick={() => setOpen(true)}
          sx={{ ml: 0.5 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => {
          if (!saving) setOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit phone number</DialogTitle>
        <DialogContent>
          <TextField
            label="Phone"
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              if (error) setError("");
            }}
            fullWidth
            margin="dense"
            required
            error={Boolean(error)}
            helperText={error || "Used for WhatsApp receipts and reminders."}
            disabled={saving}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default HallBookingsBookingPhoneEdit;
